// ─────────────────────────────────────────────────────────────────
// Glass-i — Contra Voucher frontend helpers (Stage 6B.9C).
//
// Pure-JS (no JSX, no axios) so node:test can import this directly
// from a sibling __tests__ file. The JSX components below re-export
// these constants + helpers verbatim.
// ─────────────────────────────────────────────────────────────────

// camelCase → snake_case mapping for the server-mode grid. The
// backend grid whitelist is owned by voucherPosting.service
// (VOUCHER_GRID_ALLOWED_FIELDS) — Contra reuses it, so this map is
// a strict subset that covers only the columns we actually render.
export const GRID_FIELD_TO_SQL = Object.freeze({
  voucherNo:    'voucher_no',
  voucherDate:  'voucher_date',
  status:       'status',
  totalDebit:   'total_debit',
  totalCredit:  'total_credit',
  postedAt:     'posted_at',
  createdAt:    'created_at'
});

export function mapField(c) {
  return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c;
}

function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: mapRules(out.advancedFilters.rules)
    };
  }
  return out;
}

// Transfer types — preset combinations the operator can pick to seed
// the form. They are display-only hints; the underlying voucher only
// cares about the two ledger ids and the amount.
export const TRANSFER_TYPES = Object.freeze([
  { key: 'cash_to_bank',
    label: 'Cash Deposit to Bank',
    description: 'Money moves from a cash ledger into a bank ledger.',
    fromHint: 'cash', toHint: 'bank' },
  { key: 'bank_to_cash',
    label: 'Cash Withdrawal from Bank',
    description: 'Money moves from a bank ledger into a cash ledger.',
    fromHint: 'bank', toHint: 'cash' },
  { key: 'bank_to_bank',
    label: 'Bank to Bank Transfer',
    description: 'Transfer between two bank ledgers.',
    fromHint: 'bank', toHint: 'bank' },
  { key: 'cash_to_cash',
    label: 'Cash to Cash Transfer',
    description: 'Move between two cash ledgers (multi-till).',
    fromHint: 'cash', toHint: 'cash' }
]);
export const TRANSFER_TYPE_KEYS = TRANSFER_TYPES.map((t) => t.key);

// Classify a cash/bank ledger row (as returned by /ledgers/lookups/
// by-subtype or the legacy /ledgers/cash-bank) as 'cash' / 'bank' /
// null. Used to filter the From/To dropdowns based on the chosen
// transfer type, and to detect mismatches between picked transfer
// type + selected account.
//
// Priority — Stage 6B.9B subtype first; account_group fallback for
// rows without a subtype assigned yet.
export function classifyCashOrBank(ledger) {
  if (!ledger) return null;
  if (ledger.accountSubtype === 'bank') return 'bank';
  if (ledger.accountSubtype === 'cash') return 'cash';
  const ag = String(ledger.accountGroup || '').trim().toLowerCase();
  if (ag === 'cash' || ag.startsWith('cash')) return 'cash';
  if (ag === 'bank' || ag.startsWith('bank')) return 'bank';
  return null;
}

export function inferTransferType(fromLedger, toLedger) {
  const a = classifyCashOrBank(fromLedger);
  const b = classifyCashOrBank(toLedger);
  if (!a || !b) return null;
  if (a === 'cash' && b === 'bank') return 'cash_to_bank';
  if (a === 'bank' && b === 'cash') return 'bank_to_cash';
  if (a === 'bank' && b === 'bank') return 'bank_to_bank';
  if (a === 'cash' && b === 'cash') return 'cash_to_cash';
  return null;
}

// Map backend error codes to friendly user-facing strings. Keys come
// straight from contraVoucher.service / contraVoucher.validation /
// voucherPosting.service. Unknown codes fall through to the raw
// `message` from the response.
export const ERROR_MAP = Object.freeze({
  CONTRA_VALIDATION_FAILED:    null,        // already user-readable
  CONTRA_AMOUNT_INVALID:       'Contra amount must be greater than zero.',
  CONTRA_SAME_ACCOUNT:         'From account and To account cannot be the same.',
  CONTRA_LEDGER_REQUIRED:      'Please pick both From and To accounts.',
  CONTRA_LEDGER_NOT_FOUND:     'Selected account is missing or has been deleted.',
  CONTRA_LEDGER_INACTIVE:      'Selected account is inactive.',
  CONTRA_LEDGER_NOT_CASH_BANK: 'Both accounts must be Cash or Bank ledgers.',
  CONTRA_NOT_FOUND:            'Contra voucher not found.',
  CONTRA_NOT_EDITABLE:         'Only draft contra vouchers can be edited.',
  CONTRA_NOT_DELETABLE:        'Only draft contra vouchers can be deleted.',
  VOUCHER_NOT_POSTABLE:        'Only draft contra vouchers can be posted.',
  VOUCHER_NOT_CANCELABLE:      'Only posted contra vouchers can be cancelled.',
  VOUCHER_NOT_BALANCED:        'Voucher is not balanced — total debit must equal total credit.',
  VOUCHER_PERIOD_LOCKED:       'This accounting period is locked or closed. Posting is blocked.',
  VOUCHER_PERIOD_MISSING:      'No open accounting period exists for the voucher date. Generate FY periods in Accounts Setup.',
  VOUCHER_LEDGER_INACTIVE:     'One of the selected ledgers is inactive — re-pick before posting.',
  VOUCHER_LEDGER_NOT_FOUND:    'One of the selected ledgers has been deleted.',
  VOUCHER_RACE_LOST:           'The voucher changed concurrently. Refresh and try again.'
});

export function friendlyErrorFor(err) {
  if (!err) return 'Something went wrong.';
  const body = err && err.response && err.response.data && err.response.data.error
    ? err.response.data.error : null;
  const code = body && body.code;
  if (code && Object.prototype.hasOwnProperty.call(ERROR_MAP, code) && ERROR_MAP[code]) {
    return ERROR_MAP[code];
  }
  return (body && body.message)
    || err.message
    || 'Request failed.';
}

// Build the structured payload the backend expects from the modal
// form values. Empty strings → null; amount cast to number.
export function buildCreatePayload(form) {
  const clean = (v) => {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s === '' ? null : s;
  };
  return {
    voucherDate:     form.voucherDate || null,
    fromAccountId:   form.fromAccountId || null,
    toAccountId:     form.toAccountId || null,
    amount:          form.amount === '' || form.amount == null
                       ? null : Number(form.amount),
    referenceNo:     clean(form.referenceNo),
    chequeNo:        clean(form.chequeNo),
    bankReferenceNo: clean(form.bankReferenceNo),
    narration:       clean(form.narration),
    remarks:         clean(form.remarks)
  };
}

// Client-side validation mirroring the backend Joi rules. Returns an
// array of plain-string messages (empty when the input is valid).
export function findContraFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.voucherDate)   errs.push('Voucher date is required.');
  if (!form.fromAccountId) errs.push('From account is required.');
  if (!form.toAccountId)   errs.push('To account is required.');
  if (form.fromAccountId && form.toAccountId
      && form.fromAccountId === form.toAccountId) {
    errs.push('From account and To account cannot be the same.');
  }
  const amt = Number(form.amount);
  if (!(amt > 0)) errs.push('Amount must be greater than zero.');
  if (form.narration && String(form.narration).length > 2000) {
    errs.push('Narration is too long (max 2000 characters).');
  }
  if (form.referenceNo && String(form.referenceNo).length > 100) {
    errs.push('Reference no. is too long (max 100 characters).');
  }
  if (form.chequeNo && String(form.chequeNo).length > 40) {
    errs.push('Cheque no. is too long (max 40 characters).');
  }
  if (form.bankReferenceNo && String(form.bankReferenceNo).length > 100) {
    errs.push('UTR / Bank reference no. is too long (max 100 characters).');
  }
  return errs;
}

// Status pill colour + label — single source of truth for the page +
// modal + columns. Mirrors voucherConstants but kept local so a
// future Stage 6B.9G can fork the colour scheme if needed.
export const STATUS_COLOR = Object.freeze({
  draft:     'default',
  posted:    'green',
  cancelled: 'red'
});
export const STATUS_LABEL = Object.freeze({
  draft:     'Draft',
  posted:    'Posted',
  cancelled: 'Cancelled'
});
